import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getBattery, getBatteryVendorPrices, compatibleDevices } from '../services/batteries';
import { Card } from '../components/ui/Card';
import { Spinner } from '../components/ui/Spinner';
import { SpecSheetCard } from '../components/ui/SpecSheetCard';
import { VendorTable } from '../components/ui/VendorTable';
import { DeviceThumb } from '../components/ui/DeviceThumb';

export function BatteryDetailPage() {
  const { id } = useParams<{ id: string }>();

  const batteryQ = useQuery({
    queryKey: ['battery', id],
    queryFn: () => getBattery(id!),
    enabled: !!id,
  });

  const pricesQ = useQuery({
    queryKey: ['battery', id, 'vendor-prices'],
    queryFn: () => getBatteryVendorPrices(id!),
    enabled: !!id,
  });

  const devicesQ = useQuery({
    queryKey: ['battery', id, 'devices'],
    queryFn: () => compatibleDevices(id!),
    enabled: !!id,
  });

  if (batteryQ.isLoading) return <Spinner />;
  if (batteryQ.isError || !batteryQ.data) {
    return <Card className="text-accent-red">Battery not found.</Card>;
  }

  const battery = batteryQ.data;

  return (
    <div className="flex flex-col gap-8">
      <Link to="/" className="text-sm text-neutral-400 hover:text-lime-400 w-fit">
        ← Back
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6 items-start">
        <SpecSheetCard battery={battery} />

        <div className="flex flex-col gap-6">
          {pricesQ.isLoading && <Spinner />}
          {pricesQ.isError && <Card className="text-accent-red">Could not load prices.</Card>}
          {pricesQ.isSuccess && (
            <VendorTable
              title={battery.modelCode}
              description="In the list below, you can find the best prices for compatible batteries."
              prices={pricesQ.data}
            />
          )}
        </div>
      </div>

      <section>
        <h2 className="mb-4">Compatible devices</h2>
        {devicesQ.isLoading && <Spinner />}
        {devicesQ.isError && (
          <Card className="text-accent-red">Could not load compatible devices.</Card>
        )}
        {devicesQ.isSuccess && devicesQ.data.length === 0 && (
          <Card className="text-neutral-400">No devices linked to this battery yet.</Card>
        )}
        {devicesQ.isSuccess && devicesQ.data.length > 0 && (
          <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {devicesQ.data.map((d) => (
              <li key={d._id}>
                <Link
                  to={`/devices/${d._id}`}
                  className="card flex flex-col gap-2 hover:border-lime-500 transition-colors"
                >
                  <DeviceThumb imageUrl={d.imageUrl} alt={d.name} className="aspect-square" />
                  <div className="text-xs uppercase text-neutral-400">{d.brand}</div>
                  <div className="text-sm font-semibold leading-snug">{d.name}</div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
